import { useEffect, useMemo, useState } from "react";
import config from "../config/index.js";
import ZoneIndicator from "../components/ZoneIndicator.jsx";
import { useProtocolHealth } from "../hooks/useProtocolHealth.js";
import { useTreasury } from "../hooks/useTreasury.js";
import { useTvt } from "../hooks/useTvt.js";
import { fetchInterestCoverage, fetchRecentTransactions, fetchLendingHealth } from "../services/api.js";
import { chainNowMs } from "../utils/chainClock.js";

/**
 * Protocol Health — public transparency page.
 *
 * Sourced LIVE from the backend (which in turn reads the on-chain
 * contracts). The layout/styling is the editorial spec the client
 * supplied; the numbers are real. Loading states render an em-dash so
 * the page never silently displays a stale or fabricated figure.
 */
export default function ProtocolHealth() {
    const protocolHealth = useProtocolHealth({ pollMs: 60_000 });
    const treasury = useTreasury({ pollMs: 60_000 });
    const tvt = useTvt({ pollMs: 60_000 });
    const [interestCoverage, setInterestCoverage] = useState(null);
    const [recentActivity, setRecentActivity] = useState(null);
    const [lendingHealth, setLendingHealth] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchInterestCoverage();
                if (!cancelled) setInterestCoverage(data);
            } catch {
                /* leave as null — UI shows em-dash */
            }
            try {
                const data = await fetchRecentTransactions({ limit: 250 });
                if (!cancelled) setRecentActivity(data?.items || []);
            } catch {
                /* leave as null */
            }
            try {
                const data = await fetchLendingHealth();
                if (!cancelled) setLendingHealth(data);
            } catch {
                /* leave as null */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const aggregate = useMemo(
        () => computeAggregates({
            protocolHealth: protocolHealth.data,
            treasury: treasury.data,
            tvt,
            interestCoverage,
            recentActivity,
            lendingHealth
        }),
        [protocolHealth.data, treasury.data, tvt, interestCoverage, recentActivity, lendingHealth]
    );

    return (
        <div className="w-full">
            <ProtocolHealthHero aggregate={aggregate} />
            <TrustLayer aggregate={aggregate} />
            <ViewFullBar />
            <FullProtocolHealth aggregate={aggregate} />
            <OnChainCTA />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Aggregation                                                                */
/* -------------------------------------------------------------------------- */

function computeAggregates({
    protocolHealth,
    treasury,
    tvt,
    interestCoverage,
    recentActivity,
    lendingHealth
}) {
    const ONE_USDC = 1_000_000n;

    const safeVaultBalance = bigOr(protocolHealth?.safeVault?.balance);
    const totalActivePrincipal = bigOr(protocolHealth?.deposits?.totalActivePrincipal);
    const interestReserve = bigOr(protocolHealth?.treasury?.interestReserve, treasury?.interestReserve);
    const buybackReserve = bigOr(protocolHealth?.treasury?.buybackReserve, treasury?.buybackReserve);
    const operationalReserve = bigOr(protocolHealth?.treasury?.operationalReserve, treasury?.operationalReserve);
    const treasuryAllocated = bigOr(treasury?.totalAllocated);
    const outstandingLoanPrincipal = bigOr(treasury?.outstandingLoanPrincipal, protocolHealth?.totalLoaned);

    const totalReserves = interestReserve + buybackReserve + operationalReserve;

    // Forward coverage in DAYS: interest reserve ÷ daily interest
    // obligation across all active principal at the 20% APR rate.
    const dailyInterestObligation =
        (totalActivePrincipal * BigInt(config.protocol.annualRateBps || 2000)) / (10_000n * 365n);
    const coverageDays = dailyInterestObligation > 0n
        ? Number(interestReserve / dailyInterestObligation)
        : null;

    const tvtCurrent = tvt?.current?.tvt || null;
    const tvtValid = Boolean(tvt?.current?.tvtValid);
    // Live GIL market price (USDC-per-GIL × 1e8) — shown next to the TVG floor.
    const gilSpotPrice = tvt?.current?.spotPrice ?? null;
    const tvtHistory = tvt?.history || [];
    const tvtSpark = buildSparkline(tvtHistory.map((p) => Number(p.tvt)));
    const ratchetEvents = countRatchetEvents(tvtHistory);
    const ratchetDelta30d = ratchetDelta(tvtHistory, 30);
    const allTimeLow = tvtHistory.length
        ? tvtHistory.reduce((acc, p) => (acc === null || p.tvt < acc ? p.tvt : acc), null)
        : null;

    // Chain time: the 24h/7d/30d activity windows compare against chain
    // event timestamps, so "now" must be the block clock, not wall time.
    const now = chainNowMs();
    const oneDay = 24 * 60 * 60 * 1000;
    const within = (millis, txAt) => now - new Date(txAt).getTime() <= millis;
    const items = recentActivity || [];
    const deposits24h = items.filter((x) => x.type === "deposit_opened" && within(oneDay, x.createdAt || x.timestamp)).length;
    const deposits7d = items.filter((x) => x.type === "deposit_opened" && within(7 * oneDay, x.createdAt || x.timestamp)).length;
    const deposits30d = items.filter((x) => x.type === "deposit_opened" && within(30 * oneDay, x.createdAt || x.timestamp)).length;
    const exits30d = items.filter((x) => x.type === "early_exit" && within(30 * oneDay, x.createdAt || x.timestamp)).length;
    const totalYieldPaid30d = items
        .filter((x) => (x.type === "deposit_settled" || x.type === "auto_compound") && within(30 * oneDay, x.createdAt || x.timestamp))
        .reduce((acc, x) => acc + Number(BigInt(x.amount || x.value || "0") / ONE_USDC), 0);

    return {
        ONE_USDC,
        safeVaultBalance,
        totalActivePrincipal,
        interestReserve,
        buybackReserve,
        operationalReserve,
        treasuryAllocated,
        outstandingLoanPrincipal,
        totalReserves,
        activeDepositCount: protocolHealth?.deposits?.activeCount ?? null,
        totalDepositCount: protocolHealth?.deposits?.totalCount ?? null,
        totalWallets: protocolHealth?.deposits?.totalWallets ?? null,
        coverageDays,
        coverageRatioBps: interestCoverage?.coverageRatioBps ?? null,
        coverageRequired: interestCoverage?.required ? BigInt(interestCoverage.required) : null,
        coverageActual: interestCoverage?.actual ? BigInt(interestCoverage.actual) : null,
        marketingLocked: Boolean(interestCoverage?.marketingLocked ?? protocolHealth?.marketingLocked),
        tvtCurrent,
        tvtValid,
        gilSpotPrice,
        tvtSpark,
        ratchetEvents,
        ratchetDelta30d,
        allTimeLow,
        tvtHistory,
        pegOk: protocolHealth?.pegOk ?? null,
        openCriticalAlerts: protocolHealth?.alerts?.criticalOpen ?? null,
        openWarningAlerts: protocolHealth?.alerts?.warningOpen ?? null,
        dormantCount: protocolHealth?.deposits?.dormant ?? 0,
        abandonedCount: protocolHealth?.deposits?.abandoned ?? 0,
        deposits24h,
        deposits7d,
        deposits30d,
        exits30d,
        totalYieldPaid30d,
        openLoanCount: protocolHealth?.loans?.openCount ?? 0,
        atRiskLoanCount: protocolHealth?.loans?.atRiskCount ?? 0,
        lendingTvl: lendingHealth?.tvl ? BigInt(lendingHealth.tvl) : null
    };
}

function bigOr(...values) {
    for (const v of values) {
        if (v == null) continue;
        if (typeof v === "bigint") return v;
        try {
            return BigInt(v);
        } catch {
            /* fall through */
        }
    }
    return 0n;
}

function buildSparkline(series) {
    if (!series || series.length < 2) return null;
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = max - min || 1;
    const w = 120;
    const h = 28;
    const step = w / (series.length - 1);
    const pts = series.map((v, i) => {
        const x = i * step;
        const y = h - ((v - min) / range) * (h - 4) - 2;
        return [x, y];
    });
    const line = "M" + pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L");
    const area = `${line} L${w} ${h} L0 ${h} Z`;
    return { line, area };
}

function countRatchetEvents(history) {
    if (!history || history.length < 2) return 0;
    let count = 0;
    let max = history[0].tvt;
    for (let i = 1; i < history.length; i++) {
        if (history[i].tvt > max) {
            count += 1;
            max = history[i].tvt;
        }
    }
    return count;
}

function ratchetDelta(history, days) {
    if (!history || history.length < 2) return null;
    // Chain time: cutoff is matched against chain snapshot timestamps.
    const cutoff = chainNowMs() - days * 24 * 60 * 60 * 1000;
    const past = history.find((p) => p.timestamp >= cutoff);
    const latest = history[history.length - 1];
    if (!past || !latest || past.tvt === 0n) return null;
    const delta = Number(latest.tvt - past.tvt) / Number(past.tvt);
    return delta;
}

function formatUsdc(amount, { decimals = 0 } = {}) {
    if (amount == null) return "—";
    const n = Number(amount) / 1_000_000;
    if (!Number.isFinite(n)) return "—";
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

function formatNumber(amount, opts) {
    if (amount == null) return "—";
    const n = Number(amount);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString("en-US", opts);
}

function formatTvg(tvtRaw) {
    if (tvtRaw == null) return "—";
    // tvt scaled 1e18 (USDC has 6dp; circulating supply is GIL 1e18).
    // Per-GIL backing in USDC is tvt / 1e18 with USDC being 6dp.
    // Treat tvt as USDC-per-GIL × 1e18 → divide by 1e30 to get plain USDC.
    const n = Number(tvtRaw) / 1e30;
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (n < 0.0001) return `$${n.toExponential(2)} / GIL`;
    return `$${n.toFixed(6)} / GIL`;
}

// Live GIL market price. `spotPrice` is USDC-per-GIL × 1e8 (TreasuryExecutor
// scale), so divide by 1e8. Same small-value handling as formatTvg.
function formatGilPrice(spotRaw) {
    if (spotRaw == null) return "—";
    const n = Number(spotRaw) / 1e8;
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (n < 0.0001) return `$${n.toExponential(2)} / GIL`;
    return `$${n.toFixed(6)} / GIL`;
}

function formatPercent(value, { decimals = 1 } = {}) {
    if (value == null) return "—";
    const n = Number(value) * 100;
    if (!Number.isFinite(n)) return "—";
    return `${n >= 0 ? "+" : ""}${n.toFixed(decimals)}%`;
}

function formatBps(bps) {
    if (bps == null) return "—";
    return `${(Number(bps) / 100).toFixed(1)}%`;
}

function shortAddress(addr) {
    if (!addr) return "—";
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/* -------------------------------------------------------------------------- */
/*  Hero                                                                       */
/* -------------------------------------------------------------------------- */

function ProtocolHealthHero({ aggregate }) {
    return (
        <section className="relative overflow-hidden bg-[#f6f6ec] px-5 pt-[56px] pb-[44px] md:px-8 md:pt-[80px] md:pb-[64px]">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse 600px 200px at 50% 0%, rgba(201,162,59,0.07), transparent), radial-gradient(ellipse 800px 300px at 50% 100%, rgba(12,38,24,0.04), transparent)"
                }}
            />
            <div className="relative mx-auto max-w-[880px] text-center">
                <p className="mb-5 font-manrope text-[12px] font-semibold uppercase tracking-[0.22em] text-[#b98c23]">
                    Protocol Health
                </p>
                <h1 className="mb-6 font-serif text-[40px] font-normal leading-[1.04] tracking-[-0.02em] text-[#b98c23] sm:text-[56px] md:text-[64px] lg:text-[72px]">
                    Open numbers.{" "}
                    <em className="font-light italic text-[#102712]/65">
                        Verifiable trust.
                    </em>
                </h1>
                <p className="mx-auto max-w-[620px] font-manrope text-[15px] leading-[1.55] text-[#102712] sm:text-[17px]">
                    Every metric on this page is sourced directly from on-chain
                    activity. Headline numbers up top, full transparency below,
                    and a link straight to the contracts at the bottom.
                </p>

                <div className="mt-7 inline-flex flex-wrap items-center justify-center gap-2.5">
                    <span className="inline-flex items-center gap-2.5 rounded-full border border-[#102712]/10 bg-[#102712]/[0.05] px-4 py-2 font-mono text-[12px] text-[#102712]">
                        <PulseDot variant={aggregate.pegOk === false ? "warn" : "ok"} />
                        {aggregate.pegOk === false ? "Live · USDC peg alert active" : "Live · indexed on-chain"}
                    </span>
                    <ZoneIndicator />
                </div>
            </div>
        </section>
    );
}

function PulseDot({ variant = "ok" }) {
    const palette = variant === "warn" ? "bg-[#d9a73a]" : "bg-[#6fbf7c]";
    return (
        <span className="relative inline-flex h-2 w-2 shrink-0 items-center justify-center">
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${palette} opacity-60`} />
            <span className={`relative inline-flex h-2 w-2 rounded-full ${palette}`} />
        </span>
    );
}

/* -------------------------------------------------------------------------- */
/*  Trust Layer — 5 headline metric cards                                      */
/* -------------------------------------------------------------------------- */

function TrustLayer({ aggregate }) {
    const metrics = [
        {
            label: "Forward Interest Coverage",
            value: aggregate.coverageDays != null ? aggregate.coverageDays.toLocaleString("en-US") : "—",
            unit: "Days",
            note: "Reserves on hand to meet every coupon.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        },
        {
            label: "Total Value Locked",
            value: formatUsdc(aggregate.totalActivePrincipal),
            unit: "TVL",
            note: "Aggregate principal across all Term Deposits.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        },
        {
            label: "Total Yield Paid (30d)",
            value: aggregate.totalYieldPaid30d > 0
                ? `$${aggregate.totalYieldPaid30d.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                : "—",
            unit: "USDC",
            note: "Cumulative interest distributed in the last 30 days.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        },
        {
            label: "Active Term Deposits",
            value: aggregate.activeDepositCount != null ? aggregate.activeDepositCount.toLocaleString("en-US") : "—",
            unit: "Positions",
            note: "Standard plus Turbo, currently in-term.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        },
        {
            label: "Safe Vault Balance",
            value: formatUsdc(aggregate.safeVaultBalance),
            unit: "80% of TVL",
            note: "Principal segregated and untouched.",
            sparkArea: aggregate.tvtSpark?.area,
            sparkLine: aggregate.tvtSpark?.line
        }
    ];

    return (
        <section className="relative bg-[#213e28] px-5 py-[56px] md:px-8 md:py-[88px]">
            <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{
                    background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)"
                }}
            />
            <div className="mx-auto max-w-[1280px]">
                <div className="mb-12 text-center">
                    <p className="mb-4 font-manrope text-[12px] font-semibold uppercase tracking-[0.22em] text-[#c4b050]">
                        The Trust Layer
                    </p>
                    <h2 className="font-serif text-[30px] font-normal leading-[1.1] tracking-[-0.015em] text-[#ffffff] sm:text-[38px] md:text-[46px]">
                        Five numbers that tell the whole story.
                    </h2>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {metrics.map((m, idx) => (
                        <MetricSparkCard key={m.label} metric={m} gradientId={`spark-${idx}`} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function MetricSparkCard({ metric, gradientId }) {
    return (
        <div className="group flex flex-col rounded-[14px] border border-[#ffffff]/[0.12] bg-[#162e1f] px-[22px] pt-6 pb-[22px] transition-all hover:-translate-y-0.5 hover:border-[#ffffff]/[0.22]">
            <div className="mb-4 font-manrope text-[10.5px] font-bold uppercase leading-[1.3] tracking-[0.14em] text-[#c4b050]">
                {metric.label}
            </div>
            <div className="font-serif text-[34px] font-normal leading-none tracking-[-0.02em] text-[#ffffff] sm:text-[38px]">
                {metric.value}
            </div>
            <div className="mt-1 mb-3.5 font-manrope text-[12px] font-medium uppercase tracking-[0.02em] text-[rgba(255,255,255,0.7)]">
                {metric.unit}
            </div>
            <svg
                className="mt-2 mb-2.5 h-7 w-full"
                viewBox="0 0 120 28"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0" stopColor="#c4b050" stopOpacity="0.35" />
                        <stop offset="1" stopColor="#c4b050" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {metric.sparkArea ? <path d={metric.sparkArea} fill={`url(#${gradientId})`} /> : null}
                {metric.sparkLine ? <path d={metric.sparkLine} stroke="#c4b050" strokeWidth="1.5" fill="none" /> : null}
            </svg>
            <div className="mt-auto border-t border-[#ffffff]/[0.12] pt-3 font-manrope text-[11.5px] leading-[1.4] text-[rgba(255,255,255,0.7)]">
                {metric.note}
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  View Full bar                                                              */
/* -------------------------------------------------------------------------- */

function ViewFullBar() {
    return (
        <div className="bg-[#f6f6ec] border-b-2 border-[#213e28]">
            <div className="mx-auto flex max-w-[1280px] flex-col items-start gap-4 px-5 py-9 sm:flex-row sm:items-center sm:justify-between md:px-8">
                <p className="font-serif text-[18px] font-light italic text-[#102712]">
                    Want to see the engine room? Every category, every counter
                    is below.
                </p>
                <a
                    href="#full-health"
                    className="inline-flex items-center gap-2.5 rounded-[10px] bg-[#213e28] px-5 py-3.5 font-manrope text-[14px] font-semibold text-[#ffffff] transition-all hover:-translate-y-0.5 hover:bg-[#0e2c19]"
                >
                    View Full Protocol Health
                    <svg
                        className="h-3.5 w-3.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                </a>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Full Protocol Health — 3-col grid                                          */
/* -------------------------------------------------------------------------- */

function FullProtocolHealth({ aggregate }) {
    return (
        <section
            id="full-health"
            className="bg-[#f6f6ec] px-5 py-[56px] md:px-8 md:py-[80px] lg:py-[96px]"
        >
            <div className="mx-auto max-w-[1280px]">
                <div className="mb-12 text-center">
                    <p className="mb-4 font-manrope text-[12px] font-semibold uppercase tracking-[0.22em] text-[#b98c23]">
                        Full Protocol Health
                    </p>
                    <h2 className="font-serif text-[30px] font-normal leading-[1.1] tracking-[-0.015em] text-[#b98c23] sm:text-[38px] md:text-[46px]">
                        Every counter, every category.
                    </h2>
                </div>

                <div className="grid grid-cols-1 gap-[22px] md:grid-cols-2 lg:grid-cols-3">
                    <YieldPerformanceCard aggregate={aggregate} />
                    <TreasuryHealthCard aggregate={aggregate} />
                    <RiskSafetyCard aggregate={aggregate} />
                    <RatchetChartCard aggregate={aggregate} />
                    <CapitalAllocationCard />
                    <LiquidityFeesCard aggregate={aggregate} />
                    <UserActivityCard aggregate={aggregate} />
                </div>
            </div>
        </section>
    );
}

/* ---------- card primitives ---------- */

function HealthCard({ title, subtitle, children, dark = false, className = "" }) {
    const surface = dark
        ? "bg-[#162e1f] border-[#ffffff]/[0.22] text-[#ffffff]"
        : "bg-[#f4f4ec] border-[#102712]/[0.18]";
    const titleColor = dark ? "text-[#ffffff]" : "text-[#102712]";
    const subColor = dark ? "text-[rgba(255,255,255,0.7)]" : "text-[#102712]";
    return (
        <article
            className={`flex flex-col rounded-[14px] border px-[26px] py-7 ${surface} ${className}`}
        >
            <div className="mb-5">
                <div
                    className={`mb-1.5 font-serif text-[22px] font-medium tracking-[-0.01em] ${titleColor}`}
                >
                    {title}
                </div>
                <div
                    className={`font-serif text-[13.5px] font-light italic ${subColor}`}
                >
                    {subtitle}
                </div>
            </div>
            {children}
        </article>
    );
}

function MetricRow({ label, value, trend, statusDot, dark = false }) {
    const borderColor = dark ? "border-[#ffffff]/[0.12]" : "border-[#102712]/[0.08]";
    const labelColor = dark ? "text-[#ffffff]" : "text-[#102712]";
    const valueColor = dark ? "text-[#ffffff]" : "text-[#102712]";
    return (
        <div
            className={`flex items-center justify-between border-b py-[13px] text-[14px] last:border-b-0 ${borderColor}`}
        >
            <span className={`inline-flex items-center gap-2 ${labelColor}`}>
                {statusDot ? <StatusDot variant={statusDot} /> : null}
                {label}
            </span>
            <span
                className={`inline-flex items-center font-mono text-[13.5px] font-medium tracking-[-0.01em] ${valueColor}`}
            >
                {value}
                {trend ? <Trend variant={trend.variant}>{trend.label}</Trend> : null}
            </span>
        </div>
    );
}

function Trend({ variant = "flat", children }) {
    const palette =
        variant === "up"
            ? "bg-[#6fbf7c]/[0.15] text-[#2e7a3a]"
            : variant === "down"
            ? "bg-[#c93030]/[0.15] text-[#c93030]"
            : "bg-[#102712]/[0.06] text-[#102712]";
    return (
        <span
            className={`ml-2 inline-flex items-center gap-1 rounded px-[7px] py-0.5 font-mono text-[11px] ${palette}`}
        >
            {children}
        </span>
    );
}

function StatusDot({ variant = "ok" }) {
    const palette =
        variant === "warn"
            ? "bg-[#d9a73a] ring-[#d9a73a]/30"
            : variant === "danger"
            ? "bg-[#c93030] ring-[#c93030]/30"
            : "bg-[#6fbf7c] ring-[#6fbf7c]/30";
    return (
        <span
            aria-hidden="true"
            className={`inline-block h-2 w-2 rounded-full ring-[3px] ${palette}`}
        />
    );
}

/* ---------- individual cards ---------- */

function YieldPerformanceCard({ aggregate }) {
    // 20% APR is the contractual rate (config.protocol.annualRateBps); the
    // "paid" figures should match unless the contract is in violation.
    const apr = (config.protocol.annualRateBps || 2000) / 100;
    const aprStr = `${apr.toFixed(2)}%`;
    const dailyInterest =
        (aggregate.totalActivePrincipal * BigInt(config.protocol.annualRateBps || 2000)) /
        (10_000n * 365n);
    return (
        <HealthCard
            title="Yield Performance"
            subtitle="20% APR paid from protocol reserves, not emissions."
        >
            <MetricRow label="Contractual APR" value={aprStr} />
            <MetricRow
                label="Daily interest obligation"
                value={formatUsdc(dailyInterest, { decimals: 2 })}
            />
            <MetricRow
                label="Yield paid · last 30d"
                value={
                    aggregate.totalYieldPaid30d > 0
                        ? `$${aggregate.totalYieldPaid30d.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                        : "—"
                }
            />
            <MetricRow
                label="Active deposits"
                value={
                    aggregate.activeDepositCount != null
                        ? aggregate.activeDepositCount.toLocaleString("en-US")
                        : "—"
                }
            />
        </HealthCard>
    );
}

function TreasuryHealthCard({ aggregate }) {
    const deltaVariant =
        aggregate.ratchetDelta30d == null
            ? "flat"
            : aggregate.ratchetDelta30d > 0
            ? "up"
            : aggregate.ratchetDelta30d < 0
            ? "down"
            : "flat";
    return (
        <HealthCard
            title="Market Treasury Health"
            subtitle="Shows intrinsic backing and ratchet progression."
        >
            <MetricRow
                label="Treasury balance (total allocated)"
                value={formatUsdc(aggregate.treasuryAllocated)}
            />
            <MetricRow
                label="TVG current floor"
                value={aggregate.tvtValid ? formatTvg(aggregate.tvtCurrent) : "—"}
            />
            <MetricRow
                label="GIL market price"
                value={formatGilPrice(aggregate.gilSpotPrice)}
            />
            <MetricRow
                label="TVG 30-day delta"
                value={formatPercent(aggregate.ratchetDelta30d)}
                trend={{
                    variant: deltaVariant,
                    label: deltaVariant === "up" ? "↑" : deltaVariant === "down" ? "↓" : "="
                }}
            />
            <MetricRow label="Indexed ratchet events" value={String(aggregate.ratchetEvents || 0)} />
        </HealthCard>
    );
}

function RiskSafetyCard({ aggregate }) {
    return (
        <HealthCard
            title="Risk & Safety"
            subtitle={`Answers the "what if it breaks" question.`}
        >
            <MetricRow
                statusDot={aggregate.coverageDays == null ? undefined : aggregate.coverageDays >= 365 ? "ok" : aggregate.coverageDays >= 30 ? "warn" : "danger"}
                label="Interest Reserve coverage"
                value={aggregate.coverageDays != null ? `${aggregate.coverageDays.toLocaleString("en-US")} days` : "—"}
            />
            <MetricRow
                statusDot={aggregate.coverageRatioBps == null ? undefined : aggregate.coverageRatioBps >= 10_000 ? "ok" : "warn"}
                label="Coverage ratio"
                value={formatBps(aggregate.coverageRatioBps)}
            />
            <MetricRow
                statusDot={aggregate.openCriticalAlerts === 0 ? "ok" : "danger"}
                label="Critical alerts (open)"
                value={String(aggregate.openCriticalAlerts ?? "—")}
            />
            <MetricRow
                statusDot={aggregate.pegOk === false ? "danger" : "ok"}
                label="USDC peg status"
                value={aggregate.pegOk === false ? "De-pegged" : aggregate.pegOk === true ? "Healthy" : "—"}
            />
        </HealthCard>
    );
}

function RatchetChartCard({ aggregate }) {
    const history = aggregate.tvtHistory || [];
    const tvts = history.map((p) => Number(p.tvt) / 1e30);
    const hasData = tvts.length >= 2 && tvts.some((v) => v > 0);
    const min = hasData ? Math.min(...tvts) : 0;
    const max = hasData ? Math.max(...tvts) : 0;
    const range = max - min || 1;
    const w = 720;
    const h = 200;
    const pad = 20;
    const step = hasData ? (w - pad * 2) / (history.length - 1) : 0;
    const linePts = hasData
        ? tvts.map((v, i) => [pad + i * step, h - ((v - min) / range) * (h - pad * 2) - pad])
        : [];
    const linePath = hasData
        ? "M" + linePts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L")
        : "";
    const areaPath = hasData ? `${linePath} L${(w - pad).toFixed(1)} ${h} L${pad} ${h} Z` : "";
    const last = linePts.length ? linePts[linePts.length - 1] : null;
    const allTimeLowUsdc = aggregate.allTimeLow != null ? Number(aggregate.allTimeLow) / 1e30 : null;
    const totalRise =
        hasData && min > 0 ? ((max - min) / min) : null;

    return (
        <HealthCard
            dark
            title="TVG Ratchet History"
            subtitle="The floor only moves up — never down."
            className="lg:col-span-2"
        >
            <svg
                className="my-3 w-full"
                viewBox={`0 0 ${w} 220`}
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="ratchetFill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0" stopColor="#c4b050" stopOpacity="0.35" />
                        <stop offset="1" stopColor="#c4b050" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <line x1="0" y1="40" x2={w} y2="40" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                <line x1="0" y1="100" x2={w} y2="100" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                <line x1="0" y1="160" x2={w} y2="160" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />

                {hasData ? (
                    <>
                        <path d={areaPath} fill="url(#ratchetFill)" />
                        <path d={linePath} stroke="#FFC400" strokeWidth="2" fill="none" />
                        {last ? (
                            <>
                                <circle cx={last[0]} cy={last[1]} r="5" fill="#FFC400" />
                                <circle
                                    cx={last[0]}
                                    cy={last[1]}
                                    r="9"
                                    fill="none"
                                    stroke="#FFC400"
                                    strokeOpacity="0.35"
                                    strokeWidth="1.5"
                                />
                            </>
                        ) : null}
                    </>
                ) : (
                    <text
                        x={w / 2}
                        y={h / 2}
                        textAnchor="middle"
                        fontFamily="ui-monospace, monospace"
                        fontSize="12"
                        fill="rgba(255,255,255,0.6)"
                    >
                        Awaiting first TVG snapshot
                    </text>
                )}
            </svg>
            <div className="grid grid-cols-1 gap-[18px] border-t border-[#ffffff]/[0.12] pt-[18px] sm:grid-cols-3">
                <RatchetStat
                    k="All-time low"
                    v={allTimeLowUsdc != null && allTimeLowUsdc > 0 ? `$${allTimeLowUsdc.toFixed(6)}` : "—"}
                />
                <RatchetStat
                    k="Current floor"
                    v={aggregate.tvtValid ? formatTvg(aggregate.tvtCurrent) : "—"}
                />
                <RatchetStat
                    k="Total rise"
                    v={totalRise != null ? formatPercent(totalRise) : "—"}
                />
            </div>
        </HealthCard>
    );
}

function RatchetStat({ k, v }) {
    return (
        <div>
            <div className="mb-1.5 font-manrope text-[11px] font-semibold uppercase tracking-[0.12em] text-[#c4b050]">
                {k}
            </div>
            <div className="font-serif text-[22px] font-normal tracking-[-0.01em] text-[#ffffff]">
                {v}
            </div>
        </div>
    );
}

function CapitalAllocationCard() {
    return (
        <HealthCard
            title="Capital Allocation"
            subtitle="Where every dollar of every deposit goes."
        >
            <div className="mt-1 flex flex-col items-center gap-7 sm:flex-row">
                <AllocationDonut />
                <div className="flex flex-1 flex-col">
                    <AllocationLegendRow swatch="#213e28" label="Safe Vault" value="80%" />
                    <AllocationLegendRow swatch="#c4b050" label="Token Purchase" value="10%" />
                    <AllocationLegendRow swatch="#487c3f" label="Distribution Reserve" value="9%" />
                    <AllocationLegendRow swatch="#b98c23" label="Liquidity Pool" value="1%" />
                </div>
            </div>
        </HealthCard>
    );
}

function AllocationDonut() {
    return (
        <div className="relative h-[168px] w-[168px] shrink-0">
            <svg width="168" height="168" viewBox="0 0 168 168" aria-hidden="true">
                <circle cx="84" cy="84" r="60" fill="none" stroke="rgba(33,62,40,0.15)" strokeWidth="20" />
                <circle cx="84" cy="84" r="60" fill="none" stroke="#213e28" strokeWidth="20" strokeDasharray="301.59 376.99" transform="rotate(-90 84 84)" />
                <circle cx="84" cy="84" r="60" fill="none" stroke="#c4b050" strokeWidth="20" strokeDasharray="37.7 376.99" strokeDashoffset="-301.59" transform="rotate(-90 84 84)" />
                <circle cx="84" cy="84" r="60" fill="none" stroke="#487c3f" strokeWidth="20" strokeDasharray="33.93 376.99" strokeDashoffset="-339.29" transform="rotate(-90 84 84)" />
                <circle cx="84" cy="84" r="60" fill="none" stroke="#b98c23" strokeWidth="20" strokeDasharray="3.77 376.99" strokeDashoffset="-373.22" transform="rotate(-90 84 84)" />
            </svg>
            <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
                <div>
                    <div className="font-serif text-[32px] leading-none text-[#102712]">100%</div>
                    <div className="mt-1 font-manrope text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#102712]">
                        Allocated
                    </div>
                </div>
            </div>
        </div>
    );
}

function AllocationLegendRow({ swatch, label, value }) {
    return (
        <div className="flex items-center justify-between border-b border-[#102712]/[0.08] py-2.5 last:border-b-0">
            <span className="inline-flex items-center gap-2.5 text-[13.5px] text-[#102712]">
                <span
                    aria-hidden="true"
                    className="inline-block h-[11px] w-[11px] rounded-[3px]"
                    style={{ backgroundColor: swatch }}
                />
                {label}
            </span>
            <span className="font-mono text-[13.5px] font-semibold text-[#102712]">{value}</span>
        </div>
    );
}

function LiquidityFeesCard({ aggregate }) {
    return (
        <HealthCard
            title="Reserve Buckets"
            subtitle="Tier 1 interest coverage and Tier 2 TVG defence."
        >
            <MetricRow
                label="Tier 1 — Interest Reserve"
                value={formatUsdc(aggregate.interestReserve)}
            />
            <MetricRow
                label="Tier 2 — Buyback Reserve"
                value={formatUsdc(aggregate.buybackReserve)}
            />
            <MetricRow
                label="Operational Reserve"
                value={formatUsdc(aggregate.operationalReserve)}
            />
            <MetricRow
                statusDot={aggregate.marketingLocked ? "warn" : "ok"}
                label="Marketing extraction"
                value={aggregate.marketingLocked ? "Locked" : "Open"}
            />
        </HealthCard>
    );
}

function UserActivityCard({ aggregate }) {
    return (
        <HealthCard
            title="User Activity"
            subtitle="Real usage, not promised."
        >
            <MetricRow
                label="Total wallets"
                value={aggregate.totalWallets != null ? formatNumber(aggregate.totalWallets) : "—"}
            />
            <MetricRow
                label="Total Term Deposits"
                value={aggregate.totalDepositCount != null ? formatNumber(aggregate.totalDepositCount) : "—"}
            />
            <MetricRow
                label="New deposits · last 24h"
                value={formatNumber(aggregate.deposits24h)}
            />
            <MetricRow
                label="New deposits · last 7d"
                value={formatNumber(aggregate.deposits7d)}
            />
            <MetricRow
                label="New deposits · last 30d"
                value={formatNumber(aggregate.deposits30d)}
            />
            <MetricRow
                label="Early exits · last 30d"
                value={formatNumber(aggregate.exits30d)}
            />
        </HealthCard>
    );
}

/* -------------------------------------------------------------------------- */
/*  On-chain CTA                                                               */
/* -------------------------------------------------------------------------- */

function OnChainCTA() {
    const explorer = config.network.blockExplorerUrl;
    const contracts = [
        { name: "Safe Vault", addr: config.contracts.safeVault },
        { name: "Distribution Reserve", addr: config.contracts.treasury },
        { name: "Market Treasury", addr: config.contracts.treasuryExecutor },
        { name: "Bond Contract", addr: config.contracts.bond },
        { name: "GIL Token (ERC-20)", addr: config.contracts.gil }
    ].filter((c) => c.addr);

    return (
        <section className="border-t-2 border-[#213e28] bg-[#f6f6ec] px-5 py-[72px] md:px-8 md:py-[84px]">
            <div className="mx-auto max-w-[920px] text-center">
                <p className="mb-5 font-manrope text-[12px] font-semibold uppercase tracking-[0.22em] text-[#b98c23]">
                    Don&apos;t take our word for it.
                </p>
                <h2 className="mb-4 font-serif text-[28px] font-normal leading-[1.1] tracking-[-0.015em] text-[#b98c23] sm:text-[36px] md:text-[44px]">
                    Read every transaction on-chain.
                </h2>
                <p className="mx-auto mb-9 max-w-[640px] font-manrope text-[16px] text-[#102712]">
                    The Safe Vault, Distribution Reserve, Market Treasury,
                    Bond, and GIL token contracts are all deployed on
                    {` ${config.network.name}`} and viewable on{" "}
                    {explorer ? new URL(explorer).host : "the block explorer"}.
                </p>

                <div className="mx-auto mb-8 grid max-w-[760px] grid-cols-1 gap-3.5 sm:grid-cols-2">
                    {contracts.length === 0 ? (
                        <p className="col-span-2 font-manrope text-[13px] text-[#102712]/70">
                            Contract addresses are not configured for this build.
                        </p>
                    ) : (
                        contracts.map((c) => (
                            <ContractCard key={c.name} contract={c} explorer={explorer} />
                        ))
                    )}
                </div>

                {explorer ? (
                    <a
                        href={explorer+"/blocks"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 rounded-[10px] bg-[#213e28] px-5 py-3.5 font-manrope text-[14px] font-semibold text-[#ffffff] transition-all hover:-translate-y-0.5 hover:bg-[#0e2c19]"
                    >
                        Open Block Explorer
                        <ArrowOut />
                    </a>
                ) : null}
            </div>
        </section>
    );
}

function ContractCard({ contract, explorer }) {
    const href = explorer ? `${explorer.replace(/\/$/, "")}/address/${contract.addr}` : "#";
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between rounded-[10px] border border-[#102712]/[0.15] bg-[#f4f4ec] px-[22px] py-[18px] text-left no-underline transition-all hover:-translate-y-0.5 hover:border-[#213e28]"
        >
            <div>
                <div className="mb-1 font-manrope text-[14px] font-semibold text-[#102712]">
                    {contract.name}
                </div>
                <div className="font-mono text-[12px] tracking-[-0.02em] text-[#102712]">
                    {shortAddress(contract.addr)}
                </div>
            </div>
            <span className="ml-4 shrink-0 text-[#b98c23]">
                <ArrowOut />
            </span>
        </a>
    );
}

function ArrowOut() {
    return (
        <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M7 17 L17 7 M9 7 L17 7 L17 15" />
        </svg>
    );
}